import {
	getPropertySortText,
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle
} from "client/Components";
import { convertSortDirectionToDto, DataSourceSortDirection } from "client/Data/sorting";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { generateSharePointUniqueKey } from "../../Documents/SharePoint/SharePointOverView";
import {
	DocumentGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchFolders,
	SearchIntegrationKind,
	SharePointOrderModel,
	SharePointSearchTimeScope
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { DocumentItemViewModel, SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { DocumentItemView } from "../Items/DocumentItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type FolderSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const FolderSearch = (props: FolderSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const [formData, setFormData] = useState<FolderSearchFormData>({
		...defaultFilters,
		sorting: { direction: DataSourceSortDirection.Ascending, propertyName: "name" }
	});

	const listHandle: RefObject<ScrollableVirtualListHandle<DocumentGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<DocumentGlobalSearchResultItem>>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<FolderSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sorting: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);

	const partialDataSourceOptions: Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> = useValueEqualMemo(
		() => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				timeScope: formData.timeScope
			},
			globalContext: {
				sorting: formData.sorting,
				searchVersion: searchTerm.version
			}
		}),
		[
			formData.sorting,
			formData.timeScope,
			searchTerm
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems,
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {

		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={112}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="folder-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: DocumentGlobalSearchResultItem, index: number): ReactElement => (
						<DocumentItemView
							key={index}
							navigationIndex={index}
							index={index}
							item={item}
							itemScope={folderSearchScope}
							isMobile={isMobile}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sorting")}
						options={sortOptions}
						isExpandedByDefault
					/>

					<SearchSettings.Select
						label={localize("_LastModified")}
						attachable={formHandler.for("timeScope")}
						options={timeScopeOptions}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
FolderSearch.displayName = "FolderSearch";

function getUniqueKey(item: DocumentGlobalSearchResultItem): string {
	return generateSharePointUniqueKey(item.document);
}

export const PAGE_SIZE = 20;

type Sortvalue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: StringKeyOf<Pick<DocumentItemViewModel, "name" | "modifiedOn">>;
};

type FolderSearchFormData = {
	sorting: Sortvalue;
	timeScope: SharePointSearchTimeScope;
};

type FilterModel = Omit<FolderSearchFormData, "sorting">;

const defaultFilters: FilterModel = {
	timeScope: SharePointSearchTimeScope.AnyTime
};

const sortOptions: readonly SelectOption<Sortvalue>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	}
];

const timeScopeOptions: readonly SelectOption<SharePointSearchTimeScope>[] = [
	{
		value: SharePointSearchTimeScope.AnyTime,
		text: localize("_AnyTime")
	},
	{
		value: SharePointSearchTimeScope.Last24Hrs,
		text: localize("_Past24hours")
	},
	{
		value: SharePointSearchTimeScope.LastWeek,
		text: localize("_PastWeek")
	},
	{
		value: SharePointSearchTimeScope.LastMonth,
		text: localize("_PastMonth")
	},
	{
		value: SharePointSearchTimeScope.Last3Months,
		text: localize("_Past3Months")
	},
	{
		value: SharePointSearchTimeScope.LastYear,
		text: localize("_PastYear")
	},
	{
		value: SharePointSearchTimeScope.OlderThenAYear,
		text: localize("_OlderThanAYear")
	}
];

type GlobalContext = {
	// We put sort on GlobalContext instead of the regular sorting field because the result
	// type is composite (i.e. result is not the document but rather document is a field of the result)
	sorting: {
		direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
		propertyName: FolderSearchFormData["sorting"]["propertyName"];
	};
	// Trigger new request when searchVersion changes
	searchVersion: Guid;
};

type FilterState = {
	timeScope: SharePointSearchTimeScope;
};

const useDataSource = createRemoteDataSource<
	DocumentGlobalSearchResultItem,
	FilterState,
	GlobalContext
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<DocumentGlobalSearchResultItem>> => {
				const {
					searchTerm,
					filterState,
					skip,
					amount,
					signal,
					globalContext: {
						sorting
					}
				} = options;

				const {
					timeScope
				} = filterState;

				if (searchTerm.trim() === "") {
					return {
						hasMore: false,
						items: []
					};
				} else {
					const backendResult: PageResult<DocumentGlobalSearchResultItem> = await searchFolders(
						{
							searchTerm,
							timeScope,
							skip,
							take: amount,
							order: convertSorting(sorting)
						},
						signal
					);

					const {
						items,
						totalCount
					} = backendResult;

					return {
						items,
						hasMore: items.length >= amount &&
							(skip + items.length) < totalCount
					};
				}
			}
		}
	);

const propertyOrderMap: Record<GlobalContext["sorting"]["propertyName"], SharePointColumnId> = {
	["name"]: SharePointColumnId.name,
	["modifiedOn"]: SharePointColumnId.dateModified
};

function convertSorting(
	sorting: GlobalContext["sorting"]
): SharePointOrderModel | null {
	const property: SharePointColumnId | undefined = propertyOrderMap[sorting.propertyName];
	if (isNil(property)) {
		throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
	}

	return {
		property,
		direction: convertSortDirectionToDto(sorting.direction)
	};
}

const folderSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Folders
};
